import React, { useEffect, useState } from "react";
import FieldLabel from "./FieldLabel";
import { CONDITION_OPS, NUMBER_CONDITION_OPS, TEXT_CONDITION_OPS, GROUP_HIDE_RULES, conditionNumber } from "../../../lib/bands/bandLayout";

// "Show only when…" for a field, and how a group hides with its fields
// (docs/bands-spec.md §5.5).

const OP_LABELS = {
  [CONDITION_OPS.GT]: "is more than (>)",
  [CONDITION_OPS.GTE]: "is more than or equal to (≥)",
  [CONDITION_OPS.LT]: "is less than (<)",
  [CONDITION_OPS.LTE]: "is less than or equal to (≤)",
  [CONDITION_OPS.EQ]: "equals (=)",
  [CONDITION_OPS.NE]: "doesn't equal (≠)",
  [CONDITION_OPS.IS]: "is",
  [CONDITION_OPS.IS_NOT]: "is not",
  [CONDITION_OPS.CONTAINS]: "contains",
  [CONDITION_OPS.NOT_CONTAINS]: "doesn't contain",
  [CONDITION_OPS.HAS_VALUE]: "has a value",
};

const OP_SYMBOLS = {
  [CONDITION_OPS.GT]: ">",
  [CONDITION_OPS.GTE]: "≥",
  [CONDITION_OPS.LT]: "<",
  [CONDITION_OPS.LTE]: "≤",
  [CONDITION_OPS.EQ]: "=",
  [CONDITION_OPS.NE]: "≠",
};

export const DEFAULT_CONDITION = { op: CONDITION_OPS.GT, value: 0 };

export const hasCondition = (element) => !!element?.showWhen?.op;

// Short text for a condition: "> 0", "is “Cash”", "has a value".
export function describeCondition(showWhen) {
  if (!showWhen?.op) return "";
  const { op, value } = showWhen;
  if (op === CONDITION_OPS.HAS_VALUE) return OP_LABELS[op];
  if (OP_SYMBOLS[op]) return `${OP_SYMBOLS[op]} ${conditionNumber(value) ?? 0}`;
  return `${OP_LABELS[op] || op} “${value ?? ""}”`;
}

// The condition after picking another check: the value carries over as text
// or as a number, or goes for "has a value".
function withOp(showWhen, op) {
  const next = { ...showWhen, op };
  if (op === CONDITION_OPS.HAS_VALUE) delete next.value;
  else if (NUMBER_CONDITION_OPS.includes(op)) next.value = conditionNumber(showWhen.value) ?? 0;
  else next.value = showWhen.value === undefined ? "" : String(showWhen.value);
  return next;
}

function Switch({ id, label, tip, checked, onChange }) {
  return (
    <div className="toolbar-field toolbar-field-switch">
      <FieldLabel as="div" className="toolbar-field-label" id={id} text={label} tip={tip} />
      <div className="form-check form-switch toolbar-switch">
        <input className="form-check-input" type="checkbox" role="switch" id={id + "-switch"} checked={checked} onChange={(e) => onChange(e.target.checked)} />
        <label className="form-check-label" htmlFor={id + "-switch"}>{checked ? "On" : "Off"}</label>
      </div>
    </div>
  );
}

// A number box that lets "-" be typed on the way to a number; only text that
// reads as a number is passed on.
export function NumberValue({ value, onChange }) {
  const [text, setText] = useState(String(value ?? 0));
  useEffect(() => {
    if (conditionNumber(text) !== value) setText(String(value ?? 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <input
      type="text"
      inputMode="decimal"
      className="form-control form-control-sm toolbar-input"
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        const n = conditionNumber(e.target.value);
        if (n !== null) onChange(n);
      }}
      onBlur={() => setText(String(value ?? 0))}
    />
  );
}

// Settings for one field. `onChange(showWhen)`; undefined means always show.
export function FieldVisibility({ showWhen, inGroup, onChange }) {
  const conditional = !!showWhen?.op;
  const op = showWhen?.op;
  const set = (patch) => onChange({ ...showWhen, ...patch });

  return (
    <>
      <div className="toolbar-field">
        <FieldLabel
          as="div"
          className="toolbar-field-label"
          id="visibility-show"
          text="Show"
          tip={<>&ldquo;Only when&hellip;&rdquo; prints this field only when its value passes the check, using the value before number and date formats. When it&rsquo;s hidden in a section that grows, what&rsquo;s below moves up into its place. Group it with its label (and any line or box of that row) and turn on &ldquo;Hide the whole group too&rdquo; to hide the whole row.</>}
        />
        <select
          className="form-select form-select-sm toolbar-input"
          value={conditional ? "when" : "always"}
          onChange={(e) => onChange(e.target.value === "when" ? { ...DEFAULT_CONDITION, ...(inGroup ? { hideGroup: true } : {}) } : undefined)}
        >
          <option value="always">Always</option>
          <option value="when">Only when&hellip;</option>
        </select>
      </div>

      {conditional && (
        <>
          <div className="toolbar-field">
            <div className="toolbar-field-label">The value</div>
            <select className="form-select form-select-sm toolbar-input" value={op} onChange={(e) => onChange(withOp(showWhen, e.target.value))}>
              <optgroup label="Number">
                {NUMBER_CONDITION_OPS.map((key) => <option key={key} value={key}>{OP_LABELS[key]}</option>)}
              </optgroup>
              <optgroup label="Text">
                {TEXT_CONDITION_OPS.map((key) => <option key={key} value={key}>{OP_LABELS[key]}</option>)}
              </optgroup>
              <optgroup label="Any">
                <option value={CONDITION_OPS.HAS_VALUE}>{OP_LABELS[CONDITION_OPS.HAS_VALUE]}</option>
              </optgroup>
            </select>
          </div>

          {NUMBER_CONDITION_OPS.includes(op) && (
            <div className="toolbar-field">
              <div className="toolbar-field-label">Number</div>
              <NumberValue value={conditionNumber(showWhen.value) ?? 0} onChange={(value) => set({ value })} />
            </div>
          )}
          {TEXT_CONDITION_OPS.includes(op) && (
            <div className="toolbar-field">
              <FieldLabel as="div" className="toolbar-field-label" id="visibility-text" text="Text" tip="Upper and lower case don't matter, nor do spaces at the ends." />
              <input type="text" className="form-control form-control-sm toolbar-input" value={showWhen.value ?? ""} onChange={(e) => set({ value: e.target.value })} />
            </div>
          )}

          {op !== CONDITION_OPS.HAS_VALUE && (
            <Switch
              id="visibility-missing"
              label="Hide when there's no value"
              tip="No value: empty, null, or the field missing from the data. When this is off, a field with no value skips the check and prints as usual."
              checked={showWhen.hideWhenMissing !== false}
              onChange={(on) => {
                const next = { ...showWhen };
                if (on) delete next.hideWhenMissing;
                else next.hideWhenMissing = false;
                onChange(next);
              }}
            />
          )}

          {inGroup && (
            <Switch
              id="visibility-group"
              label="Hide the whole group too"
              tip="When this field is hidden, everything grouped with it is hidden as well: its label, lines, boxes, anything. Select the whole group to choose what happens when several fields in it have conditions."
              checked={!!showWhen.hideGroup}
              onChange={(on) => {
                const next = { ...showWhen };
                if (on) next.hideGroup = true;
                else delete next.hideGroup;
                onChange(next);
              }}
            />
          )}
        </>
      )}
    </>
  );
}

// Settings for a whole group: when it hides, and which of its fields'
// conditions count. `fields` are the group's fields with a condition:
// [{ id, label, showWhen }].
export function GroupVisibility({ fields, rule, onRuleChange, onCountChange }) {
  if (fields.length === 0) {
    return (
      <div className="text-muted small">
        To hide this group, give one of its fields a condition: Alt+click the field, set &ldquo;Show&rdquo; to &ldquo;Only when&hellip;&rdquo; and turn on &ldquo;Hide the whole group too&rdquo;.
      </div>
    );
  }

  const counting = fields.filter((f) => f.showWhen.hideGroup).length;
  return (
    <>
      <div className="toolbar-field">
        <FieldLabel
          as="div"
          className="toolbar-field-label"
          id="visibility-group-rule"
          text="Hide the group when"
          tip="Only the conditions switched on below count. A field whose condition doesn't count, or whose condition fails while the group stays, still hides itself."
        />
        <select
          className="form-select form-select-sm toolbar-input"
          value={rule === GROUP_HIDE_RULES.ALL ? GROUP_HIDE_RULES.ALL : GROUP_HIDE_RULES.ANY}
          onChange={(e) => onRuleChange(e.target.value)}
          disabled={counting < 2}
        >
          <option value={GROUP_HIDE_RULES.ANY}>Any condition fails</option>
          <option value={GROUP_HIDE_RULES.ALL}>All conditions fail</option>
        </select>
        {counting < 2 && (
          <div className="text-muted small mt-1">
            Needs 2 or more visibility conditions switched on below. With {counting === 1 ? "one" : "none"}, there&rsquo;s nothing to choose between.
          </div>
        )}
      </div>
      <div className="toolbar-field-label mt-1">Conditions that count</div>
      <ul className="group-condition-list">
        {fields.map((f) => (
          <li key={f.id}>
            <div className="form-check form-switch toolbar-switch mb-0">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id={"group-count-" + f.id}
                checked={!!f.showWhen.hideGroup}
                onChange={(e) => onCountChange(f.id, e.target.checked)}
              />
              <label className="form-check-label" htmlFor={"group-count-" + f.id}>
                {f.label} <span className="text-muted">{describeCondition(f.showWhen)}</span>
              </label>
            </div>
          </li>
        ))}
      </ul>
      {counting === 0 && <div className="text-muted small">No condition counts, so the group always prints; each field still hides on its own.</div>}    </>
  );
}
